/**
 * The Release column in words (D37; `nodeRelease.ts`), shared by `acp nodes list` and the SPA (whose presentational
 * components may import the contract, not the client) so
 * the two say the same thing: a release is its short commit and the day it was published — never a raw hash, which
 * is kept for the tooltip (`releaseHashes`).
 */
import type { NodeReleaseWire, ReleaseRefWire } from './nodeRelease.js';
import type { FieldReleaseWire } from './vendor.js';

/** `3f2a1b9 · 2026-09-20`; the day is left out when the store did not say when it was published. */
export function releaseLabel(ref: ReleaseRefWire): string {
  const commit = ref.commit.slice(0, 7);
  return ref.publishedAt === null ? commit : `${commit} · ${ref.publishedAt.slice(0, 10)}`;
}

/** What the status says, for people. `null` (the cloud cannot tell) and a newer cloud's value read neutrally. */
export function releaseStatusText(view: NodeReleaseWire): string {
  switch (view.status) {
    case 'latest':
      return 'latest';
    case 'pinned':
      return 'pinned';
    case 'updating':
      return 'updating';
    case 'behind':
      return 'behind';
    case 'waiting':
      return 'update waiting';
    case 'held':
      return 'held for main';
    case 'rejected':
      return 'rejected';
    case null:
      return 'unknown';
    default:
      return 'unknown: a newer release?';
  }
}

/** The one-line cell: `3f2a1b9 · 2026-09-20 (latest)`, `updating → 9c0d2e1 · 2026-09-26`, `- (unknown)`. */
export function releaseCell(view: NodeReleaseWire): string {
  const running = view.running === null ? '-' : releaseLabel(view.running);
  const target = view.target.release;
  if (view.status === 'updating' && target !== null) return `${running} (updating → ${releaseLabel(target)})`;
  if (view.status === 'behind' && target !== null) return `${running} (behind ${releaseLabel(target)})`;
  const detail = releaseUpdateText(view);
  if (detail !== null) return `${running} (${detail})`;
  return `${running} (${releaseStatusText(view)})`;
}

/** `s` after a count unless it is one. */
const plural = (n: number, word: string): string => `${String(n)} ${word}${n === 1 ? '' : 's'}`;

/**
 * D40's update states in words, or null for the others: `update waiting: 9c0d2e1 · 2026-09-26 (2 productions active)`,
 * `held for main: 9c0d2e1 · 2026-09-26`, `rejected 9c0d2e1 · 2026-09-26: not-ready`.
 */
export function releaseUpdateText(view: NodeReleaseWire): string | null {
  if (view.status === 'waiting' && view.waiting !== undefined && view.waiting !== null) {
    return `update waiting: ${releaseLabel(view.waiting.release)} (${plural(view.waiting.productionsActive, 'production')} active)`;
  }
  if (view.status === 'held' && view.held !== undefined && view.held !== null) {
    const release = view.held.release === null ? 'the new release' : releaseLabel(view.held.release);
    return `held for main: ${release}${view.held.until === null ? '' : ` (at the latest ${view.held.until.slice(0, 16).replace('T', ' ')} UTC)`}`;
  }
  if (view.status === 'rejected') {
    const newest = view.rejections?.find((r) => r.clearedAt === null);
    if (newest !== undefined) return `rejected ${newest.release === null ? (newest.listHash?.slice(0, 12) ?? 'a release') : releaseLabel(newest.release)}: ${newest.reason}`;
  }
  return null;
}

/** One rejection in words (D40): `9c0d2e1 · 2026-09-26: not-ready at 2026-09-26T10:00:00Z — <detail>`. */
export function rejectionText(rejection: NonNullable<NodeReleaseWire['rejections']>[number]): string {
  const release = rejection.release === null ? (rejection.listHash?.slice(0, 12) ?? 'an unnamed release') : releaseLabel(rejection.release);
  const cleared = rejection.clearedAt === null ? '' : ` (cleared ${rejection.clearedAt})`;
  return `${release}: ${rejection.reason} at ${rejection.at}${cleared}${rejection.detail === '' ? '' : ` — ${rejection.detail}`}`;
}

/** The hashes behind the cell, for a tooltip or `--json`-less detail: what it runs, what it is served, and how the cloud knows. */
export function releaseHashes(view: NodeReleaseWire): string {
  const lines = [
    `runs: ${view.running === null ? 'unknown' : view.running.listHash} (${view.source === 'reported' ? 'reported by the node' : 'inferred from the packages it holds'})`,
    `served: ${view.target.release === null ? 'nothing published' : view.target.release.listHash} (${view.target.source})`,
  ];
  for (const p of view.productions) lines.push(`production ${p.productionId}: package ${p.packageHash ?? 'unknown'}`);
  return lines.join('\n');
}

/** One live production's package, in words: `01J… on 3f2a1b9 · 2026-09-20`, or its package hash when no release names it. */
export function productionReleaseText(production: NodeReleaseWire['productions'][number]): string {
  if (production.release !== null) return `${production.productionId} on ${releaseLabel(production.release)}`;
  return `${production.productionId} on package ${production.packageHash === null ? 'unknown' : production.packageHash.slice(0, 12)}`;
}

const FIELD_SOURCE_TEXT: Readonly<Record<FieldReleaseWire['sources'][number], string>> = { node: 'nodes', 'site-pin': 'a site pin', 'stage-pin': 'the stage pin', unknown: 'unknown' };

/**
 * One release in the field (D37 §4.1), for people: `3f2a1b9 · 2026-08-01 — 3 nodes, the stage pin`. The commit is the
 * list hash's first 7 when neither a node nor the list named one; `date unknown` when the cloud could not date it.
 */
export function fieldReleaseText(r: FieldReleaseWire): string {
  const name = `${(r.commit ?? r.listHash).slice(0, 7)} · ${r.publishedAt === null ? 'date unknown' : r.publishedAt.slice(0, 10)}`;
  const why = r.sources.map((s) => (s === 'node' ? `${String(r.nodes)} node${r.nodes === 1 ? '' : 's'}` : FIELD_SOURCE_TEXT[s]));
  return `${name} — ${why.length === 0 ? 'nothing' : why.join(', ')}`;
}
